import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { api } from '../../api/client';
import { unwrap, unwrapArray } from '../../lib/api-envelope';
import { LOAD_FAILURE_QUERY_OPTIONS, queryLoadFailure, queryFailureError } from '../../lib/load-failure';
import LoadFailureNotice from '../ui/LoadFailureNotice';
import { formatCalendarDate } from '../../lib/calendar-date';
import {
  MOVE_JOB_ERROR_CODES,
  isAcknowledgeable,
  moveJobRefusalOf,
  type MoveJobRefusal,
  type MoveJobResult,
} from '../../lib/ticket-move-job';
import { AlertTriangle, ArrowRight, Loader2, Lock, Search, X } from 'lucide-react';

export interface MoveTicketJobModalTicket {
  id: string;
  ticketNumber?: string | null;
  jobId: string;
  jobNumber?: string | null;
  /**
   * The ticket's OWN customer snapshot, or null when it carries none.
   *
   * `undefined` is not allowed to reach here as a stand-in for "no customer" —
   * see `effectiveCustomerId` below, where the two are kept apart deliberately.
   */
  customerId: string | null;
  customer?: string | null;
}

interface Props {
  open: boolean;
  onClose: () => void;
  ticket: MoveTicketJobModalTicket;
  onMoved: (result: MoveJobResult) => void;
}

const jobLabel = (j: any) =>
  j?.job_number || j?.jobNumber || (typeof j?.id === 'string' ? j.id.slice(0, 8) : '—');

const jobCustomerId = (j: any): string | null => j?.customer_id ?? j?.customerId ?? null;

/** One line of context under a job number, so two clones of a hitch can be told apart. */
function jobSubtitle(j: any): string {
  const parts = [
    j?.well_name || j?.wellName,
    j?.rig_name || j?.rigName,
    formatCalendarDate(j?.start_date ?? j?.startDate ?? j?.scheduled_date ?? j?.scheduledDate),
  ].filter((p) => typeof p === 'string' && p.trim() !== '');
  return parts.join(' · ');
}

/**
 * THE OFFICE DOOR FOR MOVING A TICKET TO ANOTHER JOB.
 *
 * The picker is restricted to the ticket's OWN customer because a different
 * customer is a hard refusal (`CUSTOMER_MISMATCH`), and the source job is
 * excluded because a no-op is refused too (`SAME_JOB`). Both guards stay on the
 * server; narrowing the list is so the office is not offered a choice that
 * cannot work, not a substitute for them.
 *
 * ONLY `RATE_SHEET_DIFFERS` IS RECOVERABLE. Every other refusal is terminal for
 * this attempt: the same request returns the same answer, so the modal shows
 * what happened and offers no retry. Offering one would be a control that
 * cannot work — the same dishonest-affordance class as the blind resend this
 * screen already removed.
 */
export default function MoveTicketJobModal({ open, onClose, ticket, onMoved }: Props) {
  const [toJobId, setToJobId] = useState('');
  const [reason, setReason] = useState('');
  const [filter, setFilter] = useState('');
  const [refusal, setRefusal] = useState<MoveJobRefusal | null>(null);

  useEffect(() => {
    if (!open) return;
    setToJobId('');
    setReason('');
    setFilter('');
    setRefusal(null);
  }, [open]);

  const jobsQuery = useQuery({
    queryKey: ['jobs'],
    queryFn: () => api.jobs.list().then((r) => unwrapArray<any>(r.data)),
    enabled: open,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });

  // Memoised so the `??` does not mint a fresh empty array on every render —
  // `candidates` below depends on it, and an unstable identity would recompute
  // the whole list on every keystroke in the filter.
  const jobs = useMemo<any[]>(() => jobsQuery.data ?? [], [jobsQuery.data]);
  const jobsFailure = queryLoadFailure(jobsQuery);

  /**
   * THE CUSTOMER THE DESTINATION MUST MATCH, and its THREE states are kept
   * apart on purpose.
   *
   * The API's rule is `ticket.customer_id ?? sourceJob.customer_id`, and both
   * of those may legitimately be NULL — a ticket on a job with no customer is
   * still a ticket. So:
   *
   *   - a uuid   → scope the list to it
   *   - `null`   → scope the list to jobs that ALSO have no customer
   *   - `undefined` → WE DO NOT KNOW: the source job is not in the list we
   *     loaded. Do not fall back to `null`, which would silently offer every
   *     customer-less job as a destination for a ticket whose customer is
   *     simply unread. Unknown renders as unknown and offers nothing.
   */
  const sourceJob = jobs.find((j: any) => j?.id === ticket.jobId);
  const effectiveCustomerId: string | null | undefined =
    ticket.customerId ?? (sourceJob ? jobCustomerId(sourceJob) : undefined);
  const customerUnknown = effectiveCustomerId === undefined && !jobsQuery.isLoading && !jobsFailure;

  const candidates = useMemo(() => {
    if (effectiveCustomerId === undefined) return [];
    const q = filter.trim().toLowerCase();
    return jobs
      .filter((j: any) => j?.id && j.id !== ticket.jobId)
      .filter((j: any) => jobCustomerId(j) === effectiveCustomerId)
      .filter((j: any) => {
        if (!q) return true;
        return `${jobLabel(j)} ${jobSubtitle(j)}`.toLowerCase().includes(q);
      });
  }, [jobs, filter, effectiveCustomerId, ticket.jobId]);

  const moveMutation = useMutation({
    mutationFn: (vars: { acknowledgeRateSheet?: boolean }) =>
      api.tickets.moveJob(ticket.id, {
        toJobId,
        reason: reason.trim(),
        ...(vars.acknowledgeRateSheet ? { acknowledgeRateSheet: true } : {}),
      }),
    onSuccess: (response) => {
      const d = unwrap<MoveJobResult>(response.data);
      // A 200 whose payload did not arrive is not a move we can describe. Say
      // so rather than closing on an empty outcome panel — the ticket query is
      // invalidated by the caller either way, so the screen still tells the
      // truth about where the ticket is.
      if (!d || !d.move) {
        setRefusal({
          code: null,
          status: 200,
          message:
            'The server accepted the move but sent nothing back describing it. Reload the ticket to see which job it is on.',
        });
        return;
      }
      setRefusal(null);
      onMoved(d);
    },
    onError: (err: any) => setRefusal(moveJobRefusalOf(err)),
  });

  if (!open) return null;

  const canSubmit = !!toJobId && reason.trim() !== '' && !moveMutation.isPending;

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Move ticket to another job"
        data-testid="move-job-modal"
        className="bg-fieldiq-card border border-fieldiq-border rounded-2xl w-full max-w-lg shadow-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between p-5 pb-3">
          <div>
            <h3 className="text-lg font-bold text-white">Move to another job</h3>
            <p className="text-sm text-fieldiq-text mt-0.5">
              {ticket.ticketNumber || 'This ticket'} is on{' '}
              <span className="font-semibold text-white">{ticket.jobNumber || '—'}</span>. Line items,
              prices and the ticket&apos;s own well and rig details are not changed.
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="p-1.5 rounded-lg text-fieldiq-muted hover:text-white"
          >
            <X size={16} />
          </button>
        </div>

        <div className="px-5 pb-5 space-y-4">
          {/* Destination */}
          <div>
            <label className="text-xs text-fieldiq-muted mb-1.5 block font-medium">
              Destination job {ticket.customer ? `— ${ticket.customer}` : ''}
            </label>

            {jobsFailure ? (
              <LoadFailureNotice
                error={queryFailureError(jobsQuery)}
                subject="jobs"
                allowed="admins and office staff"
              />
            ) : jobsQuery.isLoading ? (
              <div className="flex items-center gap-2 text-sm text-fieldiq-text py-6 justify-center">
                <Loader2 size={14} className="animate-spin" /> Loading jobs…
              </div>
            ) : customerUnknown ? (
              /* The source job is not in the list, so the customer this ticket
                 must stay with is unread. Offering an unscoped list here would
                 let the office pick a job the API will refuse, and — worse —
                 present that list as if it had been checked. */
              <p className="text-sm text-fieldiq-text py-4">
                This ticket&apos;s job could not be read, so the list of jobs for the same customer cannot be worked
                out. Open the ticket&apos;s job and try again from there.
              </p>
            ) : (
              <>
                <div className="relative mb-2">
                  <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-fieldiq-muted" />
                  <input
                    type="text"
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    placeholder="Filter by job number, well or rig…"
                    aria-label="Filter jobs"
                    className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg pl-8 pr-3 py-2 text-sm text-white placeholder-fieldiq-muted focus:border-fi-amber outline-none"
                  />
                </div>
                <div
                  role="radiogroup"
                  aria-label="Destination job"
                  className="border border-fieldiq-border rounded-lg divide-y divide-fieldiq-border max-h-52 overflow-y-auto"
                >
                  {candidates.length === 0 ? (
                    <p className="text-sm text-fieldiq-text p-3">
                      {filter.trim()
                        ? 'No job matches that filter.'
                        : 'No other job for this customer. A ticket can only move between jobs for the same customer.'}
                    </p>
                  ) : (
                    candidates.map((j: any) => {
                      const selected = toJobId === j.id;
                      return (
                        <button
                          key={j.id}
                          type="button"
                          role="radio"
                          aria-checked={selected}
                          onClick={() => { setToJobId(j.id); setRefusal(null); }}
                          className={`w-full text-left px-3 py-2.5 flex items-center justify-between gap-3 ${
                            selected ? 'bg-fi-amber/10' : 'hover:bg-fieldiq-dark/50'
                          }`}
                        >
                          <span className="min-w-0">
                            <span className="block text-sm font-medium text-white">{jobLabel(j)}</span>
                            {jobSubtitle(j) && (
                              <span className="block text-xs text-fieldiq-muted truncate">{jobSubtitle(j)}</span>
                            )}
                          </span>
                          {j.status && (
                            <span className="text-[10px] uppercase tracking-wide text-fieldiq-muted flex-shrink-0">
                              {String(j.status).replace(/_/g, ' ')}
                            </span>
                          )}
                        </button>
                      );
                    })
                  )}
                </div>
              </>
            )}
          </div>

          {/* Reason — required, and the modal says why */}
          <div>
            <label htmlFor="move-job-reason" className="text-xs text-fieldiq-muted mb-1.5 block font-medium">
              Reason <span className="text-fi-amber">(required)</span>
            </label>
            <textarea
              id="move-job-reason"
              value={reason}
              onChange={(e) => { setReason(e.target.value); setRefusal(null); }}
              rows={2}
              placeholder="e.g. Booked against the wrong clone of the hitch"
              className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2 text-sm text-white placeholder-fieldiq-muted focus:border-fi-amber outline-none resize-none"
            />
            <p className="text-xs text-fieldiq-muted mt-1">
              A move leaves no other trace on the ticket, so this is the only record of why it happened.
            </p>
          </div>

          {/* The refusal is cleared by ANY change to the request, because a
              refusal is about the request that produced it. A SNAPSHOT_CONFLICT
              for J-0069 left standing under a freshly-picked J-0068 would be
              read as a refusal of the new choice — which nobody has asked the
              server about yet. */}
          {refusal && <MoveJobRefusalPanel refusal={refusal} ticketId={ticket.id} />}

          <div className="flex gap-3 pt-1">
            <button
              onClick={onClose}
              className="flex-1 bg-fieldiq-dark border border-fieldiq-border text-white py-2.5 rounded-lg text-sm font-medium"
            >
              {refusal && !isAcknowledgeable(refusal) ? 'Close' : 'Cancel'}
            </button>
            {refusal && isAcknowledgeable(refusal) ? (
              <button
                onClick={() => moveMutation.mutate({ acknowledgeRateSheet: true })}
                disabled={moveMutation.isPending}
                data-testid="move-job-acknowledge-rate-sheet"
                className="flex-1 fi-action fi-action--standard fi-action--primary justify-center disabled:opacity-40"
              >
                {moveMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} />}
                Move anyway
              </button>
            ) : (
              <button
                onClick={() => moveMutation.mutate({})}
                disabled={!canSubmit}
                data-testid="move-job-submit"
                className="flex-1 fi-action fi-action--standard fi-action--primary justify-center disabled:opacity-40"
              >
                {moveMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} />}
                Move ticket
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * One refusal, rendered by CODE.
 *
 * The API writes `error` to be shown to the office verbatim, so it is always
 * printed; the per-code blocks add the detail the message cannot carry — two
 * names, a table of conflicts, a list of periods.
 */
function MoveJobRefusalPanel({ refusal, ticketId }: { refusal: MoveJobRefusal; ticketId: string }) {
  const recoverable = isAcknowledgeable(refusal);
  const Icon = refusal.status === 403 ? Lock : AlertTriangle;
  return (
    <div
      role="alert"
      data-testid="move-job-refusal"
      data-refusal-code={refusal.code ?? 'none'}
      className={`rounded-xl border p-3 space-y-2 ${
        recoverable ? 'border-fi-amber/40 bg-fi-amber/10' : 'border-fieldiq-danger/40 bg-fieldiq-danger/10'
      }`}
    >
      <div className="flex items-start gap-2">
        <Icon size={14} className="mt-0.5 flex-shrink-0 text-fieldiq-text" />
        <p className="text-sm text-white font-medium">{refusal.message}</p>
      </div>

      {refusal.code === MOVE_JOB_ERROR_CODES.stringMetresRecorded && (
        // The metres name THIS job's strings; the destination's are different
        // rows, and which one a metreage belongs to is the field's fact. So the
        // remedy is on the ticket, like SNAPSHOT_CONFLICT's.
        <p className="text-xs text-fieldiq-text pl-6">
          Clear the metres run on each casing string on the ticket first, then move it, and record them again
          against the new job's strings.{' '}
          <a href={`/tickets/${ticketId}/edit`} className="underline underline-offset-2 text-white">
            Edit this ticket
          </a>
          .
        </p>
      )}

      {refusal.code === MOVE_JOB_ERROR_CODES.customerMismatch && refusal.customer && (
        <dl className="text-xs text-fieldiq-text grid grid-cols-[auto,1fr] gap-x-3 gap-y-1 pl-6">
          <dt className="text-fieldiq-muted">This ticket</dt>
          <dd className="text-white">{refusal.customer.ticketCustomerName ?? '—'}</dd>
          <dt className="text-fieldiq-muted">Destination job</dt>
          <dd className="text-white">{refusal.customer.destinationCustomerName ?? '—'}</dd>
        </dl>
      )}

      {refusal.code === MOVE_JOB_ERROR_CODES.rateSheetDiffers && refusal.rateSheet && (
        <div className="pl-6 space-y-2">
          <dl className="text-xs text-fieldiq-text grid grid-cols-[auto,1fr] gap-x-3 gap-y-1">
            <dt className="text-fieldiq-muted">Ticket&apos;s rate sheet</dt>
            <dd className="text-white">{refusal.rateSheet.ticketRateSheetName ?? '—'}</dd>
            <dt className="text-fieldiq-muted">Destination job&apos;s</dt>
            <dd className="text-white">{refusal.rateSheet.destinationRateSheetName ?? '—'}</dd>
          </dl>
          <p className="text-xs text-fieldiq-text">
            Nothing is re-priced by a move. Every line item keeps the rate it was saved with — move it only if those
            prices are the ones you want on the destination job.
          </p>
        </div>
      )}

      {refusal.code === MOVE_JOB_ERROR_CODES.payPeriodNotOpen && refusal.payPeriods && (
        <ul className="pl-6 text-xs text-fieldiq-text space-y-1">
          {refusal.payPeriods.map((p) => (
            <li key={p.periodId}>
              <a href="/payroll/approvals" className="underline underline-offset-2 text-white">
                {formatCalendarDate(p.periodStart) ?? '—'} – {formatCalendarDate(p.periodEnd) ?? '—'}
              </a>{' '}
              <span className="text-fieldiq-muted">({p.status ?? '—'})</span>
            </li>
          ))}
        </ul>
      )}

      {refusal.code === MOVE_JOB_ERROR_CODES.snapshotConflict && refusal.conflicts && (
        <div className="pl-6 space-y-2">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-fieldiq-muted text-left">
                  <th className="py-1 pr-3 font-medium">Field</th>
                  <th className="py-1 pr-3 font-medium">Ticket has</th>
                  <th className="py-1 font-medium">Job has</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-fieldiq-border/60">
                {refusal.conflicts.map((c) => (
                  <tr key={c.field}>
                    <td className="py-1 pr-3 text-fieldiq-text">{c.label}</td>
                    <td className="py-1 pr-3 text-white">{c.ticketValue ?? '—'}</td>
                    <td className="py-1 text-white">{c.destinationJobValue ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* The remedy is on the TICKET, not here. The move does not re-snapshot
              — that is the whole reason this guard exists — so the office
              corrects or clears these on the ticket first and moves it after. */}
          <p className="text-xs text-fieldiq-text">
            Correct or clear {refusal.conflicts.length === 1 ? 'this field' : 'these fields'} on the ticket first, then
            move it.{' '}
            <a href={`/tickets/${ticketId}/edit`} className="underline underline-offset-2 text-white">
              Edit this ticket
            </a>
            .
          </p>
        </div>
      )}
    </div>
  );
}
